<template>
  <app-layout>
    <v-container class="map-and-stats geo-page">
      <h1 class="display-1">{{title}}</h1>
      <h2 class="headline" v-if="stats.payments.avg !== '$0'">{{avgPaymentHeadline}}</h2>
      <page-navigation/>
      <div class="map-and-stats__map-wrap">
        <geo-heatmap :config="heatmapCfg"/>
        <div class="container-fluid map-and-stats__stats-wrap" >
          <v-layout row>
            <v-flex sm12 md3 class="map-and-stats__stats-panel elevation-1">
              <stats-tile :visible="true" />
            </v-flex>
          </v-layout>
        </div>
      </div> <!-- END: map-and-stats__map-wrap -->

      <v-layout row justify-space-between class="hidden-sm-and-down py-3">
        <v-flex xs>
          <p class="grey--text text--darken-1">
            <a :href="siteUrls.aboutPath">Learn more</a> about doxo Payment Data.
          </p>
        </v-flex>
        <v-flex xs>
          <refresh-button :show="true" :disabled="isDisabled" class="mt-0 mr-0" @click="handleRefreshClick"/>
        </v-flex>
        <v-flex xs></v-flex>
      </v-layout>

      <providers-list
        :service-type-name="() => serviceTypeName"
        :api-path="apiPath"
        :stats="stats"
        :geo-name="() => geoName"/>
    </v-container>
  </app-layout>
</template>

<script>
  import AppLayout from '@/layouts/Default'
  import StatsTile from './components/StatsTile'
  import ProvidersList from '@/components/common/ProvidersList'
  import PageNavigation from './components/PageNavigation'
  import HeatmapLoader from '@/components/heatmap/HeatmapLoader'
  import axios from 'axios'
  import RefreshButton from '@/components/heatmap/RefreshButton.vue'
  import { eventBus } from '@/utils'
  import { serviceTypeName, apiPath } from './components/utils'
  import layoutMixin from '@/mixins/layout'
  import geoRefreshBtnMixin from '@/mixins/geo-refresh-btn'
  import { mapState, mapActions } from 'vuex'
  import siteUrls from '@/helpers/site_urls'
  import airbrake from '@/helpers/airbrake'

  export default {
    /**
     * The name of the page.
     */
    name: 'geo-show',

    mixins: [layoutMixin, geoRefreshBtnMixin],

    metaInfo () {
      // First let's remove the meta tags that may also be added by the BE because vue-meta leaves these intact: instead of replacing
      // them it actually creates new meta tags with the same name.
      const selectors = ["link[rel='canonical']"];
      ['keywords', 'description', 'og:title', 'og:description', 'og:url'].forEach(name => {
        selectors.push(`meta[name='${name}']`)
        selectors.push(`meta[property='${name}']`)
      })
      selectors.forEach(s => {
        const node = document.querySelector(s)
        if (node) {
          if (node.remove) { node.remove() } else if (node.removeNode) { node.removeNode() } // for IE
        }
      })

      // Now let's update the meta data
      const url = window.location.href.split('?')[0]
      const totalInfo = this.stats.providerCounters.total_number > 1 ? ` from over ${this.stats.providerCounters.total} companies` : ''
      const descr = `View ${this.serviceTypeName} market share and bill payment metrics for ${this.geoName}${totalInfo}. Search and compare national and local ${this.serviceTypeName} businesses serving ${this.geoName}.`

      return {
        meta: [
          { name: 'keywords', content: `${this.selectedGeo.name}, top companies, top businesses, top providers, popular providers, customer map` },
          { name: 'description', content: descr },
          { name: 'og:description', content: descr },
          { name: 'og:url', content: url },
          { name: 'og:title', content: this.title }
        ],
        link: [
          { vmid: 'canonical', rel: 'canonical', href: url }
        ],
        title: this.title
      }
    },

    data: function () {
      const heatmapCfg = {
        geoType: this.$store.state.heatmap.selectedGeo.type,
        tilesUrl: APP_CONFIG.map_tiles.base_layer.template,
        eventBusId: 'geo'
      }
      return {
        heatmapCfg: heatmapCfg,
        isDisabled: true,
        siteUrls: siteUrls,
        leftPadding: 285
      }
    },

    components: {
      AppLayout,
      RefreshButton,
      GeoHeatmap: () => ({
        component: import(/* webpackChunkName: "geo-heatmap" */ './components/GeoHeatmap'),
        loading: HeatmapLoader, // The component to use while the async component is loading
        error: {
          template: '<div class="heatmap-loader"><v-alert class="py-2 heatmap-loader--alert" type="error" value="true" icon="warning">Failed to load map.</v-alert></div>'
        }
      }),
      StatsTile,
      PageNavigation,
      ProvidersList
    },

    methods: {
      ...mapActions('geo', ['setServiceTypeAndGroup']),
      ...mapActions('heatmap', ['setMapBounds', 'setSelectedGeo', 'setHeatmapState']),
      apiPath: apiPath,
      stForSlug (slug) {
        const browser = this.stBrowser

        // eslint-disable-next-line no-unmodified-loop-condition
        for (let i = 0; browser && i < browser.length; i++) {
          let stg = browser[i]
          if (stg.slug === slug) {
            return stg
          }
          let sts = stg.children

          // eslint-disable-next-line no-unmodified-loop-condition
          for (let j = 0; sts && j < sts.length; j++) {
            let st = sts[j]
            if (st.slug === slug) {
              return st
            }
          }
        }
      }
    },

    computed: {
      ...mapState('geo', ['stBrowser', 'stats']),
      ...mapState('heatmap', ['mapBounds', 'mapBoundsChangedByUser', 'selectedGeo']),

      geoName () {
        let geo = this.selectedGeo
        let geoName = geo.name

        const geoType = geo.type
        if (geoType === 'Country') {
          geoName = 'the USA'
        } else if (geoType === 'Cbsa') {
          geoName = `${geo.name} Metro`
        } else if (geoType === 'GeoBounds') {
          geoName = 'this area'
        }
        return geoName
      },

      title () {
        return this.serviceTypeName ? `Pay your ${this.serviceTypeName} Bill in ${this.geoName}` : `Pay Bills in ${this.geoName}`
      },

      avgPaymentHeadline () {
        return `The average ${this.serviceTypeName} bill paid on doxo in ${this.geoName} was ${this.stats.payments.avg}`
      },

      serviceTypeName: serviceTypeName
    },

    mounted () {
      // TODO (mikew) When we go to a SPA, we need to make sure that this is the first page the user
      // landed on before expanding the sidebar. We only want to expand the sidebar on first load
      // See InfoPage for similar logic
      if (this.canExpandSidebar()) {
        this.$store.dispatch('layout/expand')
      }
    },

    watch: {
      '$route.params' (to, from) {
        this.isDisabled = true
        if (to.stg !== from.stg || to.st !== from.st) {
          this.setServiceTypeAndGroup({
            stg: this.stForSlug(to.stg),
            st: this.stForSlug(to.st)
          })
        }
        axios.get(`/g_api/geo_data/${to.geoId}`)
          .then(response => {
            this.setSelectedGeo(response.data.geo)
            this.isDisabled = true
          })
          .catch(err => {
            eventBus().$emit('notify', {
              group: 'main',
              type: 'error',
              title: 'Error',
              text: 'Failed to load the requested data. Try reloading the page.',
              duration: 5000
            })
            airbrake.notify(err)
          })
      },

      mapBounds () {
        if (this.mapBoundsChangedByUser) {
          this.isDisabled = false
        }
      }
    }
  }
</script>

<style lang="stylus">
  @require '~styles/bonsai/_settings'
  @require '~styles/components/_map_and_stats'
</style>



// WEBPACK FOOTER //
// frontend/src/pages/Geo/Show.vue